import { MetrikaTheme } from '@metrika/ui-kit';
import * as Sentry from '@sentry/react';
import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { appEnv, useSetFlag } from './_env';
import { GlobalAppStyle } from './App.styles';
import { Login, LoginError, LoginExpired, PrivacyLazy, TermsLazy } from './login';
import { useLogout } from './login/hooks/useLogout';
import { useIsLogged, useUserOrg } from './user/hooks';

const Logout = () => {
   const { onLogoutClick, userLoading } = useLogout();
   useEffect(() => {
      if (!userLoading) {
         const return_to = new URLSearchParams(window.location.search).get('return_to');
         onLogoutClick(return_to || '');
      }
   }, [userLoading]); // eslint-disable-line react-hooks/exhaustive-deps, react/exhaustive-effect-dependencies
   return null;
};

const AppLoginHome = () => {
   const return_to = new URLSearchParams(window.location.search).get('return_to');
   const { userLoading, isRiskUser, isLogged } = useIsLogged();

   useEffect(() => {
      if (isLogged) {
         window.location.href = return_to || (isRiskUser ? appEnv.appDomains.risk : appEnv.appDomains.login);
      }
   }, [isLogged, isRiskUser, return_to]);

   if (userLoading) {
      return null;
   }
   if (isLogged) {
      return null;
   }
   return <Redirect to="/login" />;
};

const AppLogin = () => {
   /* Scrolling / Title reset on page change */
   const { pathname } = useLocation();
   useLayoutEffect(() => {
      window.scrollTo(0, 0);
      // reset title before Helmet - handles stale document title if no title
      document.title = 'Metrika';
   }, [pathname]);

   const setFlag = useSetFlag();
   const org = useUserOrg();
   useEffect(() => {
      if (org?.name === 'metrika') {
         setFlag('debugEnabled', true);
      }
   }, [org, setFlag]);

   return (
      <Suspense fallback={null}>
         <MetrikaTheme.Provider debug={appEnv.flags.debugLayout}>
            <HelmetProvider>
               <Helmet titleTemplate="Metrika - %s" />
               {!pathname.startsWith('/web') && <MetrikaTheme.Button />}
               <GlobalAppStyle />
               <Switch>
                  <Route exact path="/terms" component={TermsLazy} />
                  <Route exact path="/privacy" component={PrivacyLazy} />
                  <Route exact path="/" component={AppLoginHome} />
                  <Route exact path="/login" component={Login} />
                  <Route exact path="/login-error" component={LoginError} />
                  <Route exact path="/login-expired" component={LoginExpired} />
                  <Route exact path="/logout" component={Logout} />
                  <Redirect to="/login" />
               </Switch>
            </HelmetProvider>
         </MetrikaTheme.Provider>
      </Suspense>
   );
};

export default Sentry.withProfiler(AppLogin);
